import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Box, Button, Card, CardContent, Stack, Text, Chip, Alert } from '@citibox-panel/ui';
import { signInWithGoogle, NonCitiboxEmailError, useAuthContext } from '@citibox-panel/auth';
import { resolveMarketIndicator } from '../../../lib/marketIndicator';
import { readRuntimeConfig } from '../../../lib/runtimeConfig';
import { sanitizeFrom } from '../lib/sanitizeFrom';

/** Returns the user-facing message for a sign-in failure, or null to stay silent. */
function mapFirebaseError(err: unknown): string | null {
  // TODO: i18n (todo el catálogo)
  if (err instanceof NonCitiboxEmailError) {
    return 'Necesitas una cuenta @citibox.com para acceder.';
  }
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { code: unknown }).code;
    if (code === 'auth/popup-closed-by-user') return null;
    if (code === 'auth/popup-blocked') {
      return 'Tu navegador bloqueó el popup. Permítelo para iniciar sesión.';
    }
    if (code === 'auth/network-request-failed') {
      return 'Sin conexión. Reintenta.';
    }
    if (code === 'auth/unauthorized-domain') {
      return 'Este dominio no está autorizado para iniciar sesión (los previews por PR no lo están). Usa dev.panel.citibox.tech.';
    }
  }
  return 'No se pudo completar el login. Inténtalo de nuevo.';
}

export function LoginPage(): ReactElement {
  const { firebaseUser, isFirebaseAvailable } = useAuthContext();
  const [searchParams] = useSearchParams();
  const dest = sanitizeFrom(searchParams.get('from'));
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const indicator = resolveMarketIndicator(readRuntimeConfig());

  useEffect(() => {
    if (firebaseUser !== null) {
      // uid enmascarado: es un identificador estable de usuario; no lo logueamos entero.
      const uid = firebaseUser.uid;
      const masked = uid.length > 6 ? `${uid.slice(0, 3)}…${uid.slice(-3)}` : '***';
      // eslint-disable-next-line no-console -- evento de observabilidad (SPEC tier A)
      console.info('[auth] signIn.success', { uid: masked });
      void navigate(dest, { replace: true });
    }
  }, [firebaseUser, dest, navigate]);

  const handleSignIn = async (): Promise<void> => {
    // Only the path: the query can carry the previous person's search tabs, which name «Usuarios»
    // by phone.
    // eslint-disable-next-line no-console -- evento de observabilidad (SPEC tier A)
    console.info('[auth] signIn.start', { from: dest.split(/[?#]/, 1)[0] ?? '/' });
    setError(null);
    setIsPending(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      const code =
        typeof err === 'object' && err !== null && 'code' in err
          ? (err as { code: string }).code
          : 'unknown';
      const errorName = err instanceof Error ? err.name : 'unknown';
      console.warn('[auth] signIn.failure', { code, errorName });
      const message = mapFirebaseError(err);
      if (message !== null) {
        setError(message);
      }
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Card variant="outlined" sx={{ minWidth: 360, p: 2 }}>
        <CardContent>
          <Stack spacing={3}>
            <Text variant="h4" component="h1">
              Citibox Panel
            </Text>
            <Chip label={indicator.text} color={indicator.degraded ? 'warning' : 'default'} />
            <Text variant="body1" color="secondary">
              Inicia sesión con tu cuenta corporativa.
            </Text>
            {!isFirebaseAvailable && (
              <Alert severity="info">
                {/* TODO: i18n */}
                Configuración de Firebase pendiente en este entorno. Avisa al equipo de infra.
              </Alert>
            )}
            {error !== null && <Alert severity="error">{error}</Alert>}
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                void handleSignIn();
              }}
              loading={isPending}
              disabled={!isFirebaseAvailable}
            >
              Continuar con Google
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
